import { generateHex, Module, RuntimeModules } from '@nu-art/ts-common';
import { ModuleFE_CSVParser } from '@nu-art/thunderstorm-frontend/modules/ModuleFE_CSVParser';
import { ModuleFE_Toaster, ModuleFE_Utils, ThunderDispatcher } from '@nu-art/thunderstorm-frontend/index';
import { DBDef_Pathway, RendererKey_PathwayHtml, RendererKey_PathwayImportErrors, RendererKey_PathwayImportSave, RendererKey_PathwayReview } from '@app/pathway-shared';
import { ModuleFE_Pathway, ModuleFE_PathwayState, ModuleFE_PathwayTransition } from '@app/pathway-frontend';
import { UtilObject_Pathway, WorkHubItem_Pathway } from '@app/pathway-frontend/search/items/pathway';
import { ModuleFE_Expression } from '@app/hcs-frontend';
import { ModuleFE_PathwayImport } from './ModuleFE_PathwayImport.js';
import { Component_PathwayImportSave } from './Component_PathwayImportSave/Component_PathwayImportSave.js';
import { Panel_PathwayImportErrors } from './Panel_PathwayImportErrors/Panel_PathwayImportErrors.js';
import { Component_PathwayHtml } from './Component_PathwayHtml/Component_PathwayHtml.js';
import { Component_PathwayReview } from './Component_PathwayReview/Component_PathwayReview.js';
import { DBDef_DiseaseProfile, RendererKey_DiseaseProfileHtml, RendererKey_DiseaseProfileImportErrors, RendererKey_DiseaseProfileImportSave, RendererKey_DiseaseProfileReview } from '@app/dp-shared';
import { ModuleFE_DiseaseProfile, ModuleFE_DiseaseValue, ModuleFE_MultiVar } from '@app/dp-frontend';
import { UtilObject_DiseaseProfile, WorkHubItem_DiseaseProfile } from '@app/dp-frontend/search/items/disease-profile';
import { ModuleFE_Reference } from '@app/hcs-frontend';
import { ModuleFE_DiseaseProfileImport } from './ModuleFE_DiseaseProfileImport.js';
import { Component_DiseaseProfileImportSave } from './Component_DiseaseProfileImportSave/Component_DiseaseProfileImportSave.js';
import { Panel_DiseaseProfileImportErrors } from './Panel_DiseaseProfileImportErrors/Panel_DiseaseProfileImportErrors.js';
import { Component_DiseaseProfileHtml } from './Component_DiseaseProfileHtml/Component_DiseaseProfileHtml.js';
import { Component_DiseaseProfileReview } from './Component_DiseaseProfileReview/Component_DiseaseProfileReview.js';
const StorageKey_VolatileImports = 'volatile-imports';
// Notifies the (separately-mounted) errors panel when an import's validation errors change — e.g.
// a blocked Save replaces them. Plain map writes don't re-render React, and the Save button lives
// in a different subtree, so the panel can't piggy-back on the button's own re-render.
export const dispatch_onVolatileImportValidationErrorsUpdated = new ThunderDispatcher('__onVolatileImportValidationErrorsUpdated');
// Coordinates the "Review" link (in the pathway details editor) with the HTML overlay (over the graph):
// they live in separate component subtrees, so a plain Set write wouldn't re-render either. Keyed by
// `${tabId}::${pathwayId}`.
export const dispatch_onPathwayReviewToggled = new ThunderDispatcher('__onPathwayReviewToggled');
// DP analog of dispatch_onVolatileImportValidationErrorsUpdated: notifies the (separately-mounted)
// DP errors panel when an import's validation errors change (e.g. a blocked Save replaces them).
export const dispatch_onDiseaseProfileImportValidationErrorsUpdated = new ThunderDispatcher('__onDiseaseProfileImportValidationErrorsUpdated');
// DP analog of dispatch_onPathwayReviewToggled: coordinates the "Review" link (in the DP editor) with
// the HTML overlay, which live in separate subtrees. Keyed by `${tabId}::${diseaseProfileId}`.
export const dispatch_onDiseaseProfileReviewToggled = new ThunderDispatcher('__onDiseaseProfileReviewToggled');
// TODO: Consider abstracting the volatile import pattern into a generic "VolatileEntityInjector"
//  that handles CSV parsing, IDB injection, manifest tracking, and tab-lifecycle cleanup —
//  decoupled from specific entity types.
class ModuleFE_VolatileImport_Class extends Module {
    _cachedManifest;
    _volatileTabIds = new Map();
    // Session-only (not serializable / not persisted): original CSV File + its validation errors, keyed by importId.
    _pendingCsvFiles = new Map();
    _validationErrors = new Map();
    // Session-only: optional HTML File the user chose to attach, keyed by importId (uploaded on Save).
    _pendingHtmlFiles = new Map();
    // Reviews default to OPEN when the pathway has HTML; we only track the ones the user explicitly
    // CLOSED (per `${tabId}::${pathwayId}`, session-only), so opening a pathway auto-shows its review.
    _closedReviews = new Set();
    // DP analogs of the pathway session-only maps (original CSV / optional HTML File + validation errors, keyed by importId).
    _pendingDpCsvFiles = new Map();
    _dpValidationErrors = new Map();
    _pendingDpHtmlFiles = new Map();
    init() {
        super.init();
        // Pathway exposes generic UI slots; data-migration owns the Save/errors/review components that fill them.
        ModuleFE_Utils.registerRenderer(RendererKey_PathwayImportSave, Component_PathwayImportSave);
        ModuleFE_Utils.registerRenderer(RendererKey_PathwayImportErrors, Panel_PathwayImportErrors);
        ModuleFE_Utils.registerRenderer(RendererKey_PathwayHtml, Component_PathwayHtml);
        ModuleFE_Utils.registerRenderer(RendererKey_PathwayReview, Component_PathwayReview);
        // Disease Profile exposes the same generic UI slots; data-migration owns the components that fill them.
        ModuleFE_Utils.registerRenderer(RendererKey_DiseaseProfileImportSave, Component_DiseaseProfileImportSave);
        ModuleFE_Utils.registerRenderer(RendererKey_DiseaseProfileImportErrors, Panel_DiseaseProfileImportErrors);
        ModuleFE_Utils.registerRenderer(RendererKey_DiseaseProfileHtml, Component_DiseaseProfileHtml);
        ModuleFE_Utils.registerRenderer(RendererKey_DiseaseProfileReview, Component_DiseaseProfileReview);
    }
    // ---- Public: tab registration ----
    registerVolatileTab = (tabId, importId) => {
        this._volatileTabIds.set(tabId, importId);
    };
    // ---- Public: deferred-save payload (the CSV the backend re-parses on commit + its dry-run errors) ----
    stashImportPayload = (importId, file, errors, htmlFile) => {
        this._pendingCsvFiles.set(importId, file);
        this._validationErrors.set(importId, errors);
        if (htmlFile)
            this._pendingHtmlFiles.set(importId, htmlFile);
        else
            this._pendingHtmlFiles.delete(importId); // re-stash without HTML clears any previously-attached file
    };
    getPendingCsvFile = (importId) => this._pendingCsvFiles.get(importId);
    getPendingHtmlFile = (importId) => this._pendingHtmlFiles.get(importId);
    // ---- Public: HTML review overlay open/close (see dispatch_onPathwayReviewToggled) ----
    reviewKey = (tabId, pathwayId) => `${tabId}::${pathwayId}`;
    isReviewOpen = (tabId, pathwayId) => !this._closedReviews.has(this.reviewKey(tabId, pathwayId));
    openReview = (tabId, pathwayId) => {
        this._closedReviews.delete(this.reviewKey(tabId, pathwayId));
        dispatch_onPathwayReviewToggled.dispatchUI(this.reviewKey(tabId, pathwayId));
    };
    closeReview = (tabId, pathwayId) => {
        this._closedReviews.add(this.reviewKey(tabId, pathwayId));
        dispatch_onPathwayReviewToggled.dispatchUI(this.reviewKey(tabId, pathwayId));
    };
    getValidationErrors = (importId) => this._validationErrors.get(importId) ?? [];
    setValidationErrors = (importId, errors) => {
        this._validationErrors.set(importId, errors);
        dispatch_onVolatileImportValidationErrorsUpdated.dispatchUI(importId);
    };
    /**
     * Commit a volatile pathway preview to KM. Lives on the module (not the Save button) because a
     * successful commit closes the preview tab and unmounts the button mid-flight; the module has no
     * React lifecycle, so the async work survives. The purge → close → hydrate → open ordering is
     * load-bearing — see inline notes. Unexpected errors throw to the caller (the button toasts them).
     */
    commitPathwayImportToKm = async (importId, tabId) => {
        const file = this.getPendingCsvFile(importId);
        if (!file)
            return { status: 'missing-file' };
        const result = await ModuleFE_PathwayImport.importPathway(file);
        if ('pathwayId' in result) {
            // ImportResult types these as DB_Object (so shared needn't depend on pathway/hcs shared);
            // cast back to the concrete DB types the FE modules expect.
            const pathway = result.pathway;
            // Two-step upload: the CSV committed above; if the user attached an HTML, upload it now and
            // patch the returned Storage URL onto the pathway. On failure the backend rolls the whole
            // import back (pathway deleted), so attachHtml rejects here — we let it propagate (the Save
            // button toasts it) WITHOUT purging/closing, leaving the volatile preview intact for a retry.
            const htmlFile = this.getPendingHtmlFile(importId);
            if (htmlFile) {
                const { pathwayHtmlUrl } = await ModuleFE_PathwayImport.attachHtml(result.pathwayId, htmlFile);
                pathway.pathwayHtmlUrl = pathwayHtmlUrl;
            }
            // Purge the volatile import + close its tab BEFORE hydrating. The volatile entities share the
            // persisted ids, and purge deletes those ids from IDB and reloads the cache — running it after
            // hydrate would wipe the entities we just seeded. Closing the tab also triggers purge via
            // __onWorkHubTabClosed, but that's a no-op once the manifest entry is gone.
            await this.purgeImport(importId);
            WorkHubItem_Pathway.closeTab(tabId);
            // The import goes through a raw XHR endpoint (not the Pathway DB-API), so nothing populated the
            // FE caches. Seed them from the persisted entities so Manager_Pathway finds the pathway
            // immediately (mirrors createChildPathway/duplicatePathway) instead of showing Editor_NewPathway.
            await Promise.all([
                ModuleFE_Pathway.onEntriesUpdated([pathway]),
                ModuleFE_PathwayState.onEntriesUpdated(result.pathwayStates),
                ModuleFE_PathwayTransition.onEntriesUpdated(result.pathwayTransitions),
                ModuleFE_Expression.onEntriesUpdated(result.resolutionFunctions),
            ]);
            UtilObject_Pathway.openEditor(pathway);
            ModuleFE_Toaster.toastSuccess('Pathway saved to KM.');
            return { status: 'success' };
        }
        this.setValidationErrors(importId, result.errors);
        ModuleFE_Toaster.toastError('Import blocked — please resolve the listed issues.');
        return { status: 'blocked', errors: result.errors };
    };
    // ---- Public: Disease Profile deferred-save payload (CSV + optional HTML + dry-run errors) ----
    stashDiseaseProfileImportPayload = (importId, file, errors, htmlFile) => {
        this._pendingDpCsvFiles.set(importId, file);
        this._dpValidationErrors.set(importId, errors);
        if (htmlFile)
            this._pendingDpHtmlFiles.set(importId, htmlFile);
        else
            this._pendingDpHtmlFiles.delete(importId); // re-stash without HTML clears any previously-attached file
    };
    getPendingDiseaseProfileCsvFile = (importId) => this._pendingDpCsvFiles.get(importId);
    getPendingDiseaseProfileHtmlFile = (importId) => this._pendingDpHtmlFiles.get(importId);
    getDiseaseProfileValidationErrors = (importId) => this._dpValidationErrors.get(importId) ?? [];
    setDiseaseProfileValidationErrors = (importId, errors) => {
        this._dpValidationErrors.set(importId, errors);
        dispatch_onDiseaseProfileImportValidationErrorsUpdated.dispatchUI(importId);
    };
    // ---- Public: Disease Profile HTML review overlay open/close (see dispatch_onDiseaseProfileReviewToggled) ----
    isDiseaseProfileReviewOpen = (tabId, diseaseProfileId) => !this._closedReviews.has(this.reviewKey(tabId, diseaseProfileId));
    openDiseaseProfileReview = (tabId, diseaseProfileId) => {
        this._closedReviews.delete(this.reviewKey(tabId, diseaseProfileId));
        dispatch_onDiseaseProfileReviewToggled.dispatchUI(this.reviewKey(tabId, diseaseProfileId));
    };
    closeDiseaseProfileReview = (tabId, diseaseProfileId) => {
        this._closedReviews.add(this.reviewKey(tabId, diseaseProfileId));
        dispatch_onDiseaseProfileReviewToggled.dispatchUI(this.reviewKey(tabId, diseaseProfileId));
    };
    /**
     * Commit a volatile disease-profile preview to KM. Mirrors {@link commitPathwayImportToKm}: lives on
     * the module (not the Save button) because a successful commit closes the preview tab and unmounts the
     * button mid-flight. The purge → close → hydrate → open ordering is load-bearing (see the pathway
     * version's inline notes). Unexpected errors throw to the caller (the button toasts them).
     */
    commitDiseaseProfileImportToKm = async (importId, tabId) => {
        const file = this.getPendingDiseaseProfileCsvFile(importId);
        if (!file)
            return { status: 'missing-file' };
        const result = await ModuleFE_DiseaseProfileImport.importDiseaseProfile(file);
        if ('diseaseProfileId' in result) {
            // DPImportResult types these as DB_Object (so shared needn't depend on dp/hcs shared);
            // cast back to the concrete DB types the FE modules expect.
            const diseaseProfile = result.diseaseProfile;
            // Two-step upload: the CSV committed above; if the user attached an HTML, upload it now and
            // patch the returned Storage URL onto the DP. On failure the backend rolls the whole import
            // back (DP deleted), so attachHtml rejects here — we let it propagate (the Save button toasts
            // it) WITHOUT purging/closing, leaving the volatile preview intact for a retry.
            const htmlFile = this.getPendingDiseaseProfileHtmlFile(importId);
            if (htmlFile) {
                const { diseaseProfileHtmlUrl } = await ModuleFE_DiseaseProfileImport.attachHtml(result.diseaseProfileId, htmlFile);
                diseaseProfile.diseaseProfileHtmlUrl = diseaseProfileHtmlUrl;
            }
            // Purge the volatile import + close its tab BEFORE hydrating (see the pathway version): the
            // volatile entities share the persisted ids, and purge deletes those ids from IDB + reloads
            // the cache — running it after hydrate would wipe the entities we just seeded.
            await this.purgeImport(importId);
            WorkHubItem_DiseaseProfile.closeTab(tabId);
            // The import goes through a raw XHR endpoint (not the DP DB-API), so nothing populated the FE
            // caches. Seed them from the persisted entities so Manager_DiseaseProfile finds the DP
            // immediately instead of showing the "new" editor.
            await Promise.all([
                ModuleFE_DiseaseProfile.onEntriesUpdated([diseaseProfile]),
                ModuleFE_MultiVar.onEntriesUpdated(result.multiVars),
                ModuleFE_DiseaseValue.onEntriesUpdated(result.diseaseValues),
                ModuleFE_Reference.onEntriesUpdated(result.references),
            ]);
            UtilObject_DiseaseProfile.openEditor(diseaseProfile);
            ModuleFE_Toaster.toastSuccess('Disease profile saved to KM.');
            return { status: 'success' };
        }
        this.setDiseaseProfileValidationErrors(importId, result.errors);
        ModuleFE_Toaster.toastError('Import blocked — please resolve the listed issues.');
        return { status: 'blocked', errors: result.errors };
    };
    // Resolve the active import for a DP preview (mirrors getImportIdForPathway): fast path is the
    // in-memory tab map; fallback is the persisted manifest by primary entity (survives a refresh).
    getImportIdForDiseaseProfile = (tabId, diseaseProfileId) => {
        const fromTab = this._volatileTabIds.get(tabId);
        if (fromTab)
            return fromTab;
        // Filter by entityType too: two imports of different entity types could share the same primary _id
        // (hex collision across collections), and we must resolve the DP import, not a pathway one.
        const entry = Object.values(this.getManifest()).find(entry => entry.entityType === DBDef_DiseaseProfile.dbKey && entry.primaryEntity._id === diseaseProfileId);
        if (!entry)
            return undefined;
        this._volatileTabIds.set(tabId, entry.importId);
        return entry.importId;
    };
    // ---- WorkHub tab listener ----
    __onWorkHubTabClosed = (tabId) => {
        const importId = this._volatileTabIds.get(tabId);
        if (!importId)
            return;
        this._volatileTabIds.delete(tabId);
        this.purgeImport(importId);
    };
    // ---- Public API ----
    importFromCSV = async (file, entityType) => {
        const text = await file.text();
        const rows = ModuleFE_CSVParser.fromString(text, { skipEmptyLines: true });
        const parsed = this.parseRows(rows);
        const grouped = this.groupByDbKey(parsed);
        // Re-import of an entity whose preview is still open: discard the previous volatile import for the
        // same primary entity FIRST. purgeImport drops its orphaned manifest entry + session-stashed files
        // and — crucially — its injected IDB copies, so the loop below sees the entity as "new" again and
        // re-injects the fresh CSV data (otherwise newIds would be empty and the editor would keep the stale
        // copy). No-op on a first import (nothing matches).
        const primaryId = grouped[entityType]?.[0]?._id;
        if (primaryId) {
            const previous = Object.values(this.getManifest()).find(entry => entry.entityType === entityType && entry.primaryEntity._id === primaryId);
            if (previous)
                await this.purgeImport(previous.importId);
        }
        const importId = generateHex(16);
        const entries = {};
        const diffMetadata = {};
        let primaryEntity;
        const writtenCollections = [];
        try {
            for (const [dbKey, items] of Object.entries(grouped)) {
                const module = this.resolveModuleByDbKey(dbKey);
                if (!module) {
                    this.logWarning(`No module found for dbKey "${dbKey}" — skipping ${items.length} rows`);
                    continue;
                }
                const ids = items.map(item => item._id);
                // New = not already in the local cache. Existing entities are real persisted data:
                // the pathway you already saved, or shared tags/refs/values the CSV carries as references.
                const newIds = ids.filter(_id => !module.cache.unique(_id));
                // Set these even when nothing is new, so the preview tab still opens on re-import.
                for (const item of items) {
                    if (item.diff)
                        diffMetadata[item._id] = item.diff;
                }
                if (dbKey === entityType && !primaryEntity)
                    primaryEntity = { dbKey, _id: items[0]._id };
                // Inject + track ONLY new entities. Injecting the CSV copy of an entity that already
                // exists would overwrite the real one in the local store (e.g. flip a saved pathway's
                // underConstruction back to the CSV's false) and purge wouldn't restore it.
                if (!newIds.length)
                    continue;
                entries[dbKey] = newIds;
                const newDocuments = items.filter(item => newIds.includes(item._id)).map(item => item.doc);
                await module.upgradeInstances(newDocuments);
                await module.IDB.syncIndexDb(newDocuments);
                writtenCollections.push({ module, ids: newIds });
            }
        }
        catch (e) {
            await this.rollbackWrittenCollections(writtenCollections);
            throw new Error(`Import failed after partial write (rolled back): ${e instanceof Error ? e.message : String(e)}`);
        }
        for (const { module } of writtenCollections)
            await module.cache.load();
        if (!primaryEntity)
            throw new Error(`No primary entity found for entityType "${entityType}" in CSV`);
        const manifestEntry = {
            importId,
            entityType,
            entries,
            primaryEntity,
            diffMetadata,
            importedAt: Date.now(),
        };
        const manifest = this.getManifest();
        manifest[importId] = manifestEntry;
        this.persistManifest(manifest);
        return manifestEntry;
    };
    purgeImport = async (importId) => {
        const manifest = this.getManifest();
        const entry = manifest[importId];
        if (!entry)
            return;
        for (const [dbKey, ids] of Object.entries(entry.entries)) {
            const module = this.resolveModuleByDbKey(dbKey);
            if (!module) {
                this.logWarning(`No module found for dbKey "${dbKey}" during purge — skipping`);
                continue;
            }
            await module.IDB.storeWrapper.deleteAll(ids.map(_id => ({ _id })));
            await module.cache.load();
        }
        delete manifest[importId];
        this.persistManifest(manifest);
        this._pendingCsvFiles.delete(importId);
        this._validationErrors.delete(importId);
        this._pendingHtmlFiles.delete(importId);
        this._pendingDpCsvFiles.delete(importId);
        this._dpValidationErrors.delete(importId);
        this._pendingDpHtmlFiles.delete(importId);
    };
    getManifest = () => {
        if (this._cachedManifest)
            return this._cachedManifest;
        const raw = localStorage.getItem(StorageKey_VolatileImports);
        if (!raw)
            return {};
        try {
            this._cachedManifest = JSON.parse(raw);
            return this._cachedManifest;
        }
        catch {
            this.logError('Corrupt volatile manifest in localStorage — resetting');
            localStorage.removeItem(StorageKey_VolatileImports);
            return {};
        }
    };
    // Resolve the active import for a pathway preview. Fast path: the in-memory tab map, set on every
    // import (even when re-importing an entity already in cache). Fallback: the persisted manifest by
    // primary entity, so the preview survives a page refresh (the tab map is memory-only, but the tab
    // itself + the manifest are restored from storage). On a manifest hit we re-link the restored tab
    // so tab-close cleanup (__onWorkHubTabClosed) keeps working afterwards.
    getImportIdForPathway = (tabId, pathwayId) => {
        const fromTab = this._volatileTabIds.get(tabId);
        if (fromTab)
            return fromTab;
        // Filter by entityType too: two imports of different entity types could share the same primary _id
        // (hex collision across collections), and we must resolve the pathway import, not a DP one.
        const entry = Object.values(this.getManifest()).find(entry => entry.entityType === DBDef_Pathway.dbKey && entry.primaryEntity._id === pathwayId);
        if (!entry)
            return undefined;
        this._volatileTabIds.set(tabId, entry.importId);
        return entry.importId;
    };
    // ---- Private ----
    parseRows = (rows) => {
        const parsed = [];
        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            if (!row.dbKey)
                continue;
            try {
                const doc = this.stripNullProperties(JSON.parse(row.document));
                const diff = row.diff ? JSON.parse(row.diff) : undefined;
                parsed.push({ _id: row._id, dbKey: row.dbKey, doc, diff });
            }
            catch (e) {
                this.logError(`Skipping row ${i} — malformed JSON: ${e instanceof Error ? e.message : String(e)}`);
            }
        }
        return parsed;
    };
    // PF CSVs serialize absent optional FKs as `null`, but KM expects them absent. `MemCache.unique(null)`
    // throws (`'_id' in null`), crashing global search + variable previews on imported data. Drop null object
    // properties so they resolve to `undefined` instead. Null slots inside arrays are preserved on purpose —
    // e.g. `rightHand`/`terms` are index-based and dropping elements would shift their meaning.
    stripNullProperties = (value) => {
        if (value === null || typeof value !== 'object')
            return value;
        if (Array.isArray(value))
            return value.map(item => this.stripNullProperties(item));
        const out = {};
        for (const [key, val] of Object.entries(value))
            if (val !== null)
                out[key] = this.stripNullProperties(val);
        return out;
    };
    groupByDbKey = (rows) => {
        const grouped = {};
        for (const row of rows)
            (grouped[row.dbKey] ??= []).push(row);
        return grouped;
    };
    rollbackWrittenCollections = async (written) => {
        for (const { module, ids } of written) {
            try {
                await module.IDB.storeWrapper.deleteAll(ids.map(_id => ({ _id })));
                await module.cache.load();
            }
            catch (e) {
                this.logError(`Rollback failed for ${module.dbDef.dbKey}: ${e instanceof Error ? e.message : String(e)}`);
            }
        }
    };
    resolveModuleByDbKey = (dbKey) => {
        return RuntimeModules().find(module => module.dbDef?.dbKey === dbKey);
    };
    persistManifest = (manifest) => {
        this._cachedManifest = Object.keys(manifest).length ? manifest : undefined;
        if (!Object.keys(manifest).length) {
            localStorage.removeItem(StorageKey_VolatileImports);
            return;
        }
        localStorage.setItem(StorageKey_VolatileImports, JSON.stringify(manifest));
    };
}
export const ModuleFE_VolatileImport = new ModuleFE_VolatileImport_Class();
//# sourceMappingURL=ModuleFE_VolatileImport.js.map